import { supabase } from '@/lib/supabase';
import { NUTRIENT_CONFIG, type NutrientKey } from '@/hooks/useMicronutrients';
import type { RecognitionFacts } from '@/utils/recognitionCard';

/**
 * Витамины и минералы ОДНОГО приёма — строкой на карточку репоста.
 *
 * ЗАЧЕМ (решение Славы 05.09.2026). До этого под БЖУ стояла строка-вывод
 * («Жиры ~43% и углеводы ~45% — калорийный набор»). Слава её забраковал,
 * и справедливо: после перевода на проценты она стала ПЕРЕСКАЗОМ тех же
 * БЖУ, что нарисованы строкой выше. Новой информации ноль.
 *
 * Микронутриенты — другое дело. Их не показывает никто, и «~45% дневного
 * железа» под фотографией обеда это то, что человек скриншотит и пересылает.
 * Заодно это ровно тот тип содержимого, который полезен ЗРИТЕЛЮ, а не только
 * автору карточки.
 *
 * ⚠️ ГЛАВНОЕ ОГРАНИЧЕНИЕ: ДО СОХРАНЕНИЯ ИХ НЕ СУЩЕСТВУЕТ.
 * Фото идёт через FAST-режим `analyze-meal`, который намеренно считает только
 * шесть полей ради скорости — это killer-фича, ломать её нельзя. Витамины
 * досчитываются после записи функцией `enrich-meal`. Замерено по 1520 позициям
 * за 30 дней: медиана 3 секунды, 90-й процентиль 4, в 15 секунд укладывается 98%.
 * Поэтому строка появляется только на карточке ИЗ ТОСТА, после сохранения,
 * а на карточке из блока под «Итого» её нет и быть не может.
 *
 * ⚠️ ТОЧНОСТЬ. Микронутриенты оцениваются грубее калорий: калории модель
 * считает по весу и составу, а железо и витамин C — по справочным значениям
 * для угаданного блюда. Поэтому проценты идут с тильдой, как и всё остальное
 * на карточке, и показываются только заметные (от 15%): «~3% магния» —
 * это шум, который лишь мусорит карточку.
 */

export type MicroChip = { emoji: string; label: string; percent: number };

/** Колонка `meal_items` для каждого нутриента */
const COLUMN: Partial<Record<NutrientKey, string>> = {
  fiber: 'fiber_g',
  omega3: 'omega3_g',
  calcium: 'calcium_mg',
  iron: 'iron_mg',
  magnesium: 'magnesium_mg',
  zinc: 'zinc_mg',
  potassium: 'potassium_mg',
  vitamin_d: 'vitamin_d_mcg',
  vitamin_b12: 'vitamin_b12_mcg',
  vitamin_c: 'vitamin_c_mg',
};

/**
 * Белок и насыщенные жиры намеренно НЕ показываем.
 * Белок уже нарисован крупно строкой БЖУ — вышло бы то же повторение,
 * из-за которого убрали предыдущую строку. Насыщенные жиры — нутриент
 * «чем меньше, тем лучше»: «~60% дневной нормы насыщенных жиров» это
 * не достижение, и хвастаться этим на карточке нельзя.
 * Овощи и фрукты не в списке: их норма в граммах, а не в микро-единицах,
 * и на карточке они читались бы как ещё один вес.
 */
const MIN_PERCENT = 15;
/** С фото места на одну строку; без фото — вертикальный список, влезает больше */
const MAX_CHIPS_PHOTO = 3;
const MAX_CHIPS_LIST = 5;

export async function getMealMicros(mealId: string, limit = MAX_CHIPS_PHOTO): Promise<MicroChip[]> {
  try {
    const cols = Object.values(COLUMN) as string[];

    const [{ data: items }, { data: summary }] = await Promise.all([
      supabase.from('meal_items').select(cols.join(',')).eq('meal_id', mealId),
      // Нормы берём из того же источника, что и экран микронутриентов, —
      // они зависят от веса и калорийности человека, зашивать их сюда нельзя.
      supabase.rpc('get_daily_nutrients_summary', {
        p_date: new Date().toISOString().slice(0, 10),
      }),
    ]);

    if (!items?.length || !summary) return [];

    const targets = (summary as any).targets ?? {};
    const chips: MicroChip[] = [];

    for (const [key, column] of Object.entries(COLUMN) as [NutrientKey, string][]) {
      const target = Number(targets[key]?.amount || 0);
      if (target <= 0) continue;

      const sum = items.reduce((acc: number, it: any) => acc + Number(it[column] || 0), 0);
      if (sum <= 0) continue;

      const percent = Math.round((sum / target) * 100);
      if (percent < MIN_PERCENT) continue;

      const cfg = NUTRIENT_CONFIG[key];
      chips.push({ emoji: cfg.emoji, label: cfg.label, percent });
    }

    // Сильнейшие вперёд: на карточке помещается три, и это должны быть те,
    // ради которых её вообще стоит показывать.
    return chips.sort((a, b) => b.percent - a.percent).slice(0, limit);
  } catch (e) {
    console.error('[getMealMicros]', e);
    // Молча ничего не показываем: отсутствие строки на карточке — не ошибка,
    // а её отсутствие лучше, чем сорванный репост.
    return [];
  }
}

/**
 * Факты для карточки из ДНЕВНИКА — приём уже сохранён и досчитан.
 *
 * ЗАЧЕМ (решение Славы 05.09.2026): «при сканировании фото в моменте
 * он отправляет только КБЖУ, так и оставим, но в дневник надо функцию
 * добавить». Правильно: в момент съёмки витаминов ещё нет физически,
 * а в Дневнике они есть всегда.
 *
 * ⚠️ ФОТО ЗДЕСЬ НЕ БУДЕТ, и это нормально. Снимки еды мы намеренно
 * не храним; замерено — фото есть у 17 приёмов из 4343. Карточка без
 * фото рисуется другой раскладкой, где витамины идут крупным списком,
 * а под ними состав приёма (см. recognitionCard.ts).
 */
export async function getMealShareFacts(mealId: string): Promise<RecognitionFacts | null> {
  try {
    const [{ data: meal }, { data: items }, micros] = await Promise.all([
      supabase
        .from('meals')
        .select('meal_type, total_calories, total_protein_g, total_fat_g, total_carbs_g')
        .eq('id', mealId)
        .maybeSingle(),
      supabase.from('meal_items').select('name').eq('meal_id', mealId),
      getMealMicros(mealId, MAX_CHIPS_LIST),
    ]);

    if (!meal) return null;

    const names = (items ?? []).map((i: any) => i.name).filter(Boolean);
    const title = MEAL_TYPE_LABEL[String(meal.meal_type)] ?? 'Приём пищи';

    return {
      photo: null,
      title,
      calories: Number(meal.total_calories) || 0,
      protein: Number(meal.total_protein_g) || 0,
      fat: Number(meal.total_fat_g) || 0,
      carbs: Number(meal.total_carbs_g) || 0,
      micros,
      composition: names.join(' · '),
    };
  } catch (e) {
    console.error('[getMealShareFacts]', e);
    return null;
  }
}

const MEAL_TYPE_LABEL: Record<string, string> = {
  breakfast: 'Завтрак',
  lunch: 'Обед',
  dinner: 'Ужин',
  snack: 'Перекус',
};
